import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { memo, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import GuestCounterControls from './GuestCounterControls';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} [props.label] - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {string} [props.placeholder] - The placeholder for the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string | React.ReactNode | any} [props.helperText] - The helper text for the input field
 * @param {boolean} [props.fullWidth] - The full width state of the input field
 * @param {boolean} [props.required] - The required state of the input field
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/material/InputLabel').InputLabelProps} [props.inputLabelProps] - The props for the input label
 * @param {Omit<import('@mui/material/TextField').TextFieldProps, 'name'>} [props.textFieldProps] - The props for the text field
 */

const ControlledGuestCountSelectComponent = ({
  name,
  control,
  guestOptions,
  countLabels,
  totalMaximum,
  label = '',
  required = false,
  placeholder = '',
  error = false,
  helperText = '',
  fullWidth = false,
  controllerOptions = {},
  inputLabelProps = {},
  textFieldProps = {},
}) => {
  const intl = useIntl();

  const [anchorElement, setAnchorElement] = useState(null);
  const [popperWidth, setPopperWidth] = useState(undefined);

  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  const id = `id_${name}`;
  inputLabelProps.htmlFor = id;
  textFieldProps.id = id;

  const open = Boolean(anchorElement);
  const closeLabel = intl.formatMessage({ id: 'ControlledGuestCountSelect.close' });

  const handleToggle = (event) => {
    if (anchorElement) {
      setAnchorElement(null);
    } else {
      const target = event.currentTarget;
      setAnchorElement(target);
      setPopperWidth(target.clientWidth);  // Capture width on open → prevents any jump
    }
  };

  const handleClickAway = (event) => {
    if (anchorElement && anchorElement.contains(event.target)) {
      return;  // Ignore clicks on the anchor itself
    }
    setAnchorElement(null);
  };

  useEffect(() => {
    if (!open) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setAnchorElement(null);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open]);

  const getDisplayValue = (value = {}) => {
    const parts = Object.keys(value)
      .map((key) => {
        const count = value[key];
        if (count === 0) return null;
        return countLabels[key](count);
      })
      .filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : placeholder;
  };

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <Stack spacing={1} width="100%">
          {label ? (
            <InputLabel htmlFor={id} required={required} shrink={false} {...inputLabelProps}>
              {label}
            </InputLabel>
          ) : null}
          <TextField
            error={error}
            fullWidth={fullWidth}
            helperText={helperText}
            id={id}
            onClick={handleToggle}
            placeholder={placeholder}
            required={required}
            size="small"
            {...field}
            {...textFieldProps}
            slotProps={{
              input: {
                endAdornment: open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />,
                readOnly: true,
                sx: {
                  '&:hover': {
                    cursor: 'pointer',
                  },
                },
              },
              htmlInput: {
                sx: {
                  fontSize: '0.9rem',
                },
              },
            }}
            value={getDisplayValue(field.value)}
          />
          <Popper
            anchorEl={anchorElement}
            disablePortal={true}
            modifiers={[
              {
                name: 'flip',
                enabled: false,
                options: {
                  altBoundary: false,
                  rootBoundary: 'document',
                  padding: 8,
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: false,
                  altBoundary: true,
                  tether: true,
                  rootBoundary: 'document',
                  padding: 8,
                },
              },
            ]
            }
            open={open}
            placement="bottom-start"
            sx={{ zIndex: 2 }}  // Matches Popover z-index
          >
            <ClickAwayListener onClickAway={handleClickAway}>
              <Paper
                elevation={8}
                sx={{
                  width: popperWidth ?? 'auto',  // Fixed captured width
                  // Tablet fields are wide; keep the counters next to their labels. The desktop panel is unchanged.
                  maxWidth: { sm: 400, lg: 'none' },
                  mt: 0.5,  // Small gap like Popover
                }}
              >
                <Stack spacing={2} sx={{ p: 2 }}>
                  <GuestCounterControls
                    guestOptions={guestOptions}
                    onChange={field.onChange}
                    totalMaximum={totalMaximum}
                    value={field.value}
                  />
                  <Box display="flex" justifyContent="flex-end" width="100%">
                    <Button
                      color="secondary"
                      onClick={() => setAnchorElement(null)}
                      variant="text"
                    >
                      {closeLabel}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Stack>
      )}
    />
  );
};

ControlledGuestCountSelectComponent.displayName = 'ControlledGuestCountSelect';

const ControlledGuestCountSelect = memo(ControlledGuestCountSelectComponent);

export default ControlledGuestCountSelect;
